import React, { useCallback } from 'react';
import { Anchor, Box, Inscription, StatementsDocumentIcon, useFormContext } from '@common-fe/common-fe';

import { AgreementSection, BlockWrapper } from '@/modules/ExpressSetup/ExpressSetupSignUp/components';
import {
  CreateMainContactField,
  CreateMainContactForm,
} from '@/modules/ExpressSetup/ExpressSetupSignUp/hooks/useCreateMainContactForm';

interface Props {
  partnerName?: string;
  privacyPolicyLink?: string;
  termsAndConditionsLink?: string;
}

const ConsentToPartnership: React.FC<Props> = ({
  partnerName,
  privacyPolicyLink,
  termsAndConditionsLink,
}) => {
  const {
    setValue,
    watch,
    clearErrors,
    formState: { errors },
  } = useFormContext<CreateMainContactForm>();

  const isPricingConsentAgreed = watch(CreateMainContactField.isPricingConsentAgreed);
  const isPrivacyPolicyAgreed = watch(CreateMainContactField.isPrivacyPolicyAgreed);

  const handleSetIsPricingConsentAgreed = useCallback(
    (value: boolean) => {
      setValue(CreateMainContactField.isPricingConsentAgreed, value);
      if (value) {
        clearErrors(CreateMainContactField.isPricingConsentAgreed);
      }
    },
    [setValue, clearErrors]
  );

  const handleSetIsPrivacyPolicyAgreed = useCallback(
    (value: boolean) => {
      setValue(CreateMainContactField.isPrivacyPolicyAgreed, value);
      if (value) {
        clearErrors(CreateMainContactField.isPrivacyPolicyAgreed);
      }
    },
    [setValue, clearErrors]
  );

  return (
    <BlockWrapper Icon={StatementsDocumentIcon} title="Consent To Partnership">
      <Box>
        <AgreementSection
          title="Pricing Consent"
          isChecked={isPricingConsentAgreed}
          setIsChecked={handleSetIsPricingConsentAgreed}
          isError={
            !!errors?.[CreateMainContactField.isPricingConsentAgreed]?.message && !isPricingConsentAgreed
          }
          testId="create-main-contact_pricing-consent"
          checkboxTitle={
            <Inscription size="large">
              I have read and acknowledge {partnerName}’s{' '}
              <Anchor href={termsAndConditionsLink} target="_blank" style={{ fontWeight: 400 }}>
                Pricing Consent
              </Anchor>
            </Inscription>
          }
        />
      </Box>
      <Box margin={{ top: 'spacing24' }}>
        <AgreementSection
          title="Privacy Policy & Terms of Use"
          description="We care about data privacy and security. By using the Site, you agree to be bound by our Privacy Policy posted on the Site, which is incorporated into these Terms of Use. Please be advised the Site is hosted in the United States."
          isChecked={isPrivacyPolicyAgreed}
          setIsChecked={handleSetIsPrivacyPolicyAgreed}
          isError={
            !!errors?.[CreateMainContactField.isPrivacyPolicyAgreed]?.message && !isPrivacyPolicyAgreed
          }
          testId="create-main-contact_privacy-policy"
          checkboxTitle={
            <Inscription size="large">
              I have read and acknowledge {partnerName}{' '}
              <Anchor href={privacyPolicyLink} target="_blank" style={{ fontWeight: 400 }}>
                Privacy Policy & Terms of Use
              </Anchor>
            </Inscription>
          }
        />
      </Box>
    </BlockWrapper>
  );
};

export default ConsentToPartnership;
